import * as THREE from 'three';

export const HasteShader = {
    name: 'HasteShader',

    uniforms: {
        tDiffuse: { value: null },
        resolution: { value: new THREE.Vector2(1920, 1080) },

        // === DRAG-BASED SPEED EFFECT ===
        // These are updated based on user drag velocity
        dragSpeed: { value: 0.0 },           // Magnitude of drag velocity (0-1+)
        dragDirection: { value: new THREE.Vector2(0.0, 0.0) }, // Normalized drag direction

        // === IDLE ANIMATION SETTINGS ===
        idleIntensity: { value: 1.0 },       // Base intensity for idle effects (0-1)

        // core speed controls (base values)
        baseSpeed: { value: 0.3 },           // Minimum speed effect even when idle
        shakeAmount: { value: 0.001 },

        // motion blur
        radialBlur: { value: 0.25 },         // 0–1
        linearBlur: { value: 0.15 },

        // chromatic speed-warp
        chromaShift: { value: 0.005 },

        // gamma
        gamma: { value: 0.7 },

        // time (animated) - for idle effects
        time: { value: 0.0 },

        // === ANIME SPEED LINES SETTINGS ===
        speedLinesEnabled: { value: 1.0 },   // 1.0 = enabled, 0.0 = disabled
        speedLinesIntensity: { value: 0.8 }, // Opacity of speed lines (0-1)
        speedLinesCount: { value: 60.0 },    // Number of speed lines
        speedLinesLength: { value: 0.4 },    // How far lines extend from edge (0-1)
        speedLinesWidth: { value: 0.066 },   // Width of each line
        speedLinesSpeed: { value: 3.0 },     // Animation speed

        // === WARM COLOR OVERLAY ===
        warmOverlayIntensity: { value: 0.4 }, // Overall intensity of warm effect (0-1)
        warmColorInner: { value: new THREE.Vector3(1.0, 0.85, 0.3) },  // Yellow/gold at center
        warmColorOuter: { value: new THREE.Vector3(1.0, 0.4, 0.1) },   // Orange/red at edges
    },

    vertexShader: /* glsl */`
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
    `,

    fragmentShader: /* glsl */`
        uniform sampler2D tDiffuse;
        uniform vec2 resolution;

        // Drag-based
        uniform float dragSpeed;
        uniform vec2 dragDirection;

        // Idle animation
        uniform float idleIntensity;
        uniform float baseSpeed;
        uniform float shakeAmount;

        uniform float radialBlur;
        uniform float linearBlur;
        uniform float chromaShift;

        uniform float gamma;
        uniform float time;

        // Anime speed lines
        uniform float speedLinesEnabled;
        uniform float speedLinesIntensity;
        uniform float speedLinesCount;
        uniform float speedLinesLength;
        uniform float speedLinesWidth;
        uniform float speedLinesSpeed;

        // Warm color overlay
        uniform float warmOverlayIntensity;
        uniform vec3 warmColorInner;   // Yellow/orange at center
        uniform vec3 warmColorOuter;   // Red/orange at edges

        varying vec2 vUv;

        // --------------------------------------
        // Hash-based noise
        // --------------------------------------
        float rand(vec2 co) {
            return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
        }

        // --------------------------------------
        // Smooth noise for animations
        // --------------------------------------
        float noise(float x) {
            float i = floor(x);
            float f = fract(x);
            return mix(rand(vec2(i, 0.0)), rand(vec2(i + 1.0, 0.0)), smoothstep(0.0, 1.0, f));
        }

        // --------------------------------------
        // Anime-style speed lines from rectangular screen edges
        // --------------------------------------
        float speedLines(vec2 uv, float time, float intensity) {
            // Skip if no intensity (not dragging)
            if (intensity < 0.01) return 0.0;
            
            vec2 center = vec2(0.5, 0.5);
            vec2 toCenter = center - uv;
            float angle = atan(toCenter.y, toCenter.x);
            
            // Calculate distance to rectangular edge (not circular)
            // This gives us how far we are from the nearest edge
            vec2 fromCenter = abs(uv - center); // Distance from center in each axis
            float distToEdgeX = 0.5 - fromCenter.x; // Distance to left/right edge
            float distToEdgeY = 0.5 - fromCenter.y; // Distance to top/bottom edge
            float distToEdge = min(distToEdgeX, distToEdgeY); // Nearest edge distance
            
            // Convert angle to 0-1 range for line distribution
            float normalizedAngle = (angle + 3.14159) / (2.0 * 3.14159);
            
            // Create lines based on angle
            float lineIndex = floor(normalizedAngle * speedLinesCount);
            float linePhase = fract(normalizedAngle * speedLinesCount);
            
            // Each line has its own random properties
            float lineRand = rand(vec2(lineIndex, 0.0));
            float lineRand2 = rand(vec2(lineIndex, 1.0));
            
            // Animate line length over time - each line pulses independently
            float pulseSpeed = speedLinesSpeed * (0.5 + lineRand);
            float pulse = sin(time * pulseSpeed + lineRand * 6.28) * 0.5 + 0.5;
            
            // Line length scales with drag intensity
            float dynamicLength = speedLinesLength * intensity * (0.5 + lineRand2 * 0.5) * (0.5 + pulse * 0.5);
            
            // Line starts from edge (distToEdge = 0) and extends toward center
            float lineStart = 0.0;
            float lineEnd = dynamicLength;
            
            // Is this pixel within the line's radial extent?
            float inLineRadial = smoothstep(lineStart, lineStart + 0.01, distToEdge) * 
                                 (1.0 - smoothstep(lineEnd - 0.02, lineEnd, distToEdge));
            
            // Is this pixel within the line's angular width?
            float lineWidth = speedLinesWidth * (0.3 + lineRand * 0.7);
            float inLineAngular = 1.0 - smoothstep(0.0, lineWidth, abs(linePhase - 0.5));
            
            // Fade out towards center
            float fadeFactor = 1.0 - smoothstep(0.0, dynamicLength, distToEdge);
            
            // Extra fade near corners to avoid clutter
            float cornerFade = smoothstep(0.0, 0.1, min(distToEdgeX, distToEdgeY) + 0.05);
            
            return inLineRadial * inLineAngular * fadeFactor * cornerFade;
        }

        void main() {
            vec2 uv = vUv;

            // Combine drag speed with base idle speed
            // dragSpeed comes from actual user interaction
            // baseSpeed provides minimum "always haste" feeling
            float idlePulse = (sin(time * 2.0) * 0.5 + 0.5) * 0.3; // Gentle pulse when idle
            float effectiveSpeed = max(dragSpeed, baseSpeed + idlePulse * idleIntensity);
            
            // Use drag direction when dragging, otherwise use animated direction
            vec2 effectiveDirection;
            if (dragSpeed > 0.01) {
                effectiveDirection = dragDirection;
            } else {
                // Gentle swirl animation when idle
                float angleOffset = time * 0.5;
                effectiveDirection = normalize(vec2(
                    cos(angleOffset) * 0.5,
                    sin(angleOffset) * 0.3 - 0.7
                ));
            }

            // --------------------------------------
            // 1. Screen shake (subtle, based on speed)
            // --------------------------------------
            float shakeScale = shakeAmount * effectiveSpeed;
            uv += (rand(uv + time) - 0.5) * shakeScale;

            // --------------------------------------
            // 2. Linear motion blur (direction-based)
            // --------------------------------------
            vec3 linearCol = vec3(0.0);
            int samples = 8;
            float linearBlurAmount = linearBlur * effectiveSpeed;
            for(int i = 0; i < samples; i++) {
                float t = float(i) / float(samples - 1);
                vec2 offset = effectiveDirection * linearBlurAmount * t;
                linearCol += texture2D(tDiffuse, uv - offset).rgb;
            }
            linearCol /= float(samples);

            // --------------------------------------
            // 3. Radial speed streaks (tunnel effect)
            // --------------------------------------
            vec2 center = vec2(0.5, 0.5);
            vec2 dirFromCenter = uv - center;
            float distFromCenter = length(dirFromCenter);
            vec2 normalizedDir = distFromCenter > 0.001 ? dirFromCenter / distFromCenter : vec2(0.0);

            vec3 radialCol = vec3(0.0);
            float radialBlurAmount = radialBlur * effectiveSpeed;
            for(int i = 0; i < samples; i++) {
                float t = float(i) / float(samples - 1);
                vec2 offset = normalizedDir * radialBlurAmount * t;
                radialCol += texture2D(tDiffuse, uv - offset).rgb;
            }
            radialCol /= float(samples);

            // --------------------------------------
            // 4. Chromatic aberration (speed warp)
            // Direction-aware: shift along movement direction
            // --------------------------------------
            float c = chromaShift * effectiveSpeed;
            vec3 chroma;
            chroma.r = texture2D(tDiffuse, uv + effectiveDirection * c).r;
            chroma.g = texture2D(tDiffuse, uv).g;
            chroma.b = texture2D(tDiffuse, uv - effectiveDirection * c).b;

            // --------------------------------------
            // 5. Combine motion effects
            // Weight effects more when actually dragging
            // --------------------------------------
            vec3 base = texture2D(tDiffuse, uv).rgb;

            // More blur when dragging fast
            float linearWeight = mix(0.2, 0.5, smoothstep(0.0, 1.0, dragSpeed));
            float radialWeight = mix(0.15, 0.4, smoothstep(0.0, 1.0, dragSpeed));
            float chromaWeight = mix(0.1, 0.3, smoothstep(0.0, 1.0, dragSpeed));

            vec3 result = mix(base, linearCol, linearWeight);
            result = mix(result, radialCol, radialWeight);
            result = mix(result, chroma, chromaWeight);

            // --------------------------------------
            // 6. Anime speed lines overlay (only when dragging)
            // --------------------------------------
            if (speedLinesEnabled > 0.5 && dragSpeed > 0.05) {
                // Intensity based on drag speed - lines appear and grow with speed
                float linesActivation = smoothstep(0.05, 0.5, dragSpeed);
                float lines = speedLines(vUv, time, linesActivation);
                
                // Lines intensity scales with drag speed
                float linesIntensity = speedLinesIntensity * linesActivation;
                
                // White/light blue speed lines
                vec3 lineColor = vec3(0.9, 0.95, 1.0);
                result = mix(result, lineColor, lines * linesIntensity);
            }

            // --------------------------------------
            // 7. Warm color overlay (yellow/orange/red)
            // Subtle fiery effect that intensifies with speed
            // --------------------------------------
            float warmIntensity = 1.0 + warmOverlayIntensity * effectiveSpeed;
            
            // Gradient from center (yellow) to edges (red/orange)
            vec3 warmColor = mix(warmColorInner, warmColorOuter, distFromCenter * 1.5);
            
            // Apply as a subtle overlay - use soft light blending for a natural look
            // Soft light: enhances contrast while adding color
            vec3 warmBlend = result * (1.0 + warmColor * warmIntensity * 0.5);
            
            // Also add a slight additive glow at edges for that fiery rim
            float edgeGlow = smoothstep(0.3, 0.7, distFromCenter) * warmIntensity * 0.15;
            warmBlend += warmColorOuter * edgeGlow;
            
            result = mix(result, warmBlend, warmIntensity * 0.6);

            // --------------------------------------
            // 8. Vignette enhancement at high speed
            // --------------------------------------
            float vignette = 1.0 - distFromCenter * effectiveSpeed * 0.3;
            vignette = clamp(vignette, 0.7, 1.0);
            result *= vignette;

            // --------------------------------------
            // 9. Gamma correction
            // --------------------------------------
            result = pow(result, vec3(gamma));

            gl_FragColor = vec4(result, 1.0);
        }
    `,
};

export function createHasteUniforms(
    resolution = new THREE.Vector2(1920, 1080)
) {
    return {
        tDiffuse: { value: null },
        resolution: { value: resolution.clone() },

        dragSpeed: { value: 0.0 },
        dragDirection: { value: new THREE.Vector2(0, 0) },

        idleIntensity: { value: 1.0 },
        baseSpeed: { value: 0.3 },
        shakeAmount: { value: 0.001 },

        radialBlur: { value: 0.25 },
        linearBlur: { value: 0.15 },
        chromaShift: { value: 0.005 },

        gamma: { value: 0.7 },
        time: { value: 0 },

        speedLinesEnabled: { value: 1.0 },
        speedLinesIntensity: { value: 0.6 },
        speedLinesCount: { value: 40.0 },
        speedLinesLength: { value: 0.3 },
        speedLinesWidth: { value: 0.008 },
        speedLinesSpeed: { value: 2.0 },

        warmOverlayIntensity: { value: 0.4 },
        warmColorInner: { value: new THREE.Vector3(1.0, 0.85, 0.3) },
        warmColorOuter: { value: new THREE.Vector3(1.0, 0.4, 0.1) },
    };
}
